import React, { useEffect } from 'react';
import { ProgramSummary } from 'spw-esa-projects/program-summary';
import { useParams } from 'react-router-v5';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentProject,
  selectCurrentBuilding,
  currentFloor as selectCurrentFloor,
  selectCurrentProjectFloors,
} from 'spw-redux/modules/projects/selectors';
import {
  actions as projectActions,
  setCurrentBuilding,
  setCurrentFloor,
  setCurrentProjectColors,
} from 'spw-redux/modules/projects/actions';
import { IBuilding } from 'spw-redux/modules/projects/types';
import { omit } from 'ramda';
import { useLocation } from 'react-router-dom-v5';
import {
  ID_TYPE,
  setSummarySelectedIdType,
} from 'spw-redux/modules/program-summary/actions';
import { setCurrentSpaceTypeColors } from 'spw-redux/modules/space_types/actions';
import { selectProjectSpaceTypes } from 'spw-redux/modules/space_types/selectors';

type Params = {
  projectId?: string;
  buildingId?: string;
  floorId?: string;
};

const ProgramSummaryExportable: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const params: Params = useParams();

  const location = useLocation();
  const project = useSelector(currentProject);
  const projectFloors = useSelector(selectCurrentProjectFloors);
  const building = useSelector(selectCurrentBuilding);
  const currentFloor = useSelector(selectCurrentFloor);
  const projectSpaceTypes = useSelector(selectProjectSpaceTypes);
  const searchParams = new URLSearchParams(location?.search);
  const downloadParams: {
    showRsf?: boolean;
    clientFacingNamesAreVisible?: boolean;
  } = {};
  const showRsf = Boolean(searchParams.get('show_rsf') === 'true');
  const clientFacingNamesAreVisible = Boolean(
    searchParams.get('show_client_facing_names') === 'true'
  );

  if (showRsf) {
    downloadParams.showRsf = showRsf;
  }

  if (clientFacingNamesAreVisible) {
    downloadParams.clientFacingNamesAreVisible = clientFacingNamesAreVisible;
  }

  useEffect(() => {
    const spaceTypeColors = localStorage.getItem('spaceTypeColors');
    const currentProjectColors = localStorage.getItem('currentProjectColors');

    if (projectSpaceTypes?.length && spaceTypeColors) {
      dispatch(setCurrentSpaceTypeColors(JSON.parse(spaceTypeColors)));
    }

    if (currentProjectColors) {
      dispatch(setCurrentProjectColors(JSON.parse(currentProjectColors)));
    }
  }, [dispatch, projectSpaceTypes]);

  useEffect(() => {
    if (!project?.id && params.projectId) {
      const projectId = parseInt(params.projectId, 10);

      dispatch(projectActions.getProjectInfoById(projectId));
    }
  }, [project?.id, params?.projectId, dispatch]);

  useEffect(() => {
    if (project?.id && params?.buildingId && !building?.id) {
      const buildingId = parseInt(params.buildingId, 10);

      const currentBuilding = project.buildings.find(
        (projectBuilding: IBuilding) => projectBuilding.id === buildingId
      );
      dispatch(setCurrentBuilding(currentBuilding));
    }
  }, [
    project?.id,
    project.buildings,
    params?.buildingId,
    building?.id,
    dispatch,
  ]);

  useEffect(() => {
    if (params?.floorId && project?.id && !currentFloor?.id) {
      const floorId = parseInt(params.floorId, 10);
      const floor = projectFloors[floorId];
      const { building: currentBuilding } = floor;

      dispatch(setSummarySelectedIdType(floor.id, ID_TYPE.floorId));
      dispatch(setCurrentBuilding(currentBuilding));
      dispatch(setCurrentFloor(omit(['building'], floor)));
    }
  }, [params?.floorId, project?.id, projectFloors, currentFloor, dispatch]);

  return (
    <div className="content" style={{ background: 'white' }}>
      <ProgramSummary isRenderingForExport downloadParams={downloadParams} />
    </div>
  );
};

export default ProgramSummaryExportable;
